import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  FiHome, FiFolder, FiCheckSquare, FiUsers,
  FiClock, FiBarChart2, FiSettings, FiLogOut,
  FiCalendar, FiDollarSign, FiUserCheck, FiBriefcase,
  FiBell, FiExternalLink, FiActivity, FiLayout, FiCheckCircle, FiFileText,
  FiTrendingUp, FiShield, FiUserPlus, FiChevronDown, FiChevronRight,
  FiAward, FiHome as FiWfh, FiMessageSquare, FiPlusCircle, FiClipboard,
} from 'react-icons/fi';
import { notificationAPI, companyAPI } from '../services/api';
import api from '../services/api';
import './Sidebar.css';

/* ── Nav group definitions ─────────────────────────────── */
// perm: module key checked via hasPermission() for team members (admin bypasses, client uses fixed list)
const NAV_GROUPS = [
  {
    key: 'client_main',
    label: null,
    items: [
      { path: '/client-portal', icon: <FiHome />,          label: 'Dashboard',     roles: ['client'] },
      { path: '/projects',      icon: <FiFolder />,        label: 'Projects',      roles: ['client'] },
      { path: '/tasks',         icon: <FiCheckSquare />,   label: 'Tasks',         roles: ['client'] },
      { path: '/my-invoices',   icon: <FiFileText />,      label: 'My Invoices',   roles: ['client'] },
      { path: '/messages',      icon: <FiMessageSquare />, label: 'Messages',      roles: ['client'], isMessageBadge: true },
      { path: '/notifications', icon: <FiBell />,          label: 'Notifications', roles: ['client'], isBadge: true },
      { path: '/settings',      icon: <FiSettings />,      label: 'Settings',      roles: ['client'] },
    ],
  },
  {
    key: 'main',
    label: null,
    items: [
      { path: '/dashboard', icon: <FiHome />,        label: 'Dashboard', roles: ['admin','manager','team'], perm: 'dashboard' },
      { path: '/projects',    icon: <FiFolder />,      label: 'Projects',    roles: ['admin','manager','team'], perm: 'projects' },
      { path: '/tasks',          icon: <FiCheckSquare />, label: 'Tasks',          roles: ['admin','manager','team'], perm: 'tasks' },
      { path: '/daily-work',     icon: <FiClipboard />,   label: 'Daily Work',     roles: ['admin','manager'], perm: 'daily_work' },
      { path: '/task-templates', icon: <FiLayout />,      label: 'Task Templates', roles: ['admin','manager'], perm: 'task_templates' },
    ],
  },
  {
    key: 'hr',
    label: 'HR & People',
    items: [
      { path: '/team',        icon: <FiUsers />,      label: 'Team',        roles: ['admin','manager','team'], perm: 'team' },
      { path: '/attendance',  icon: <FiUserCheck />,  label: 'Attendance',  roles: ['admin','manager','team'], perm: 'attendance' },
      { path: '/leaves',      icon: <FiCalendar />,   label: 'Leaves',      roles: ['admin','manager','team'], perm: 'leaves' },
      { path: '/salary',      icon: <FiDollarSign />, label: 'Salary',      roles: ['admin','manager','team'], perm: 'salary' },
      { path: '/holidays',    icon: <FiBriefcase />,  label: 'Holidays',    roles: ['admin','manager','team'], perm: 'holidays' },
      { path: '/performance', icon: <FiAward />,      label: 'Performance', roles: ['admin','manager','team'], perm: 'performance' },
      { path: '/onboarding',  icon: <FiUserPlus />,   label: 'Onboarding',  roles: ['admin','manager','team'], perm: 'onboarding' },
      { path: '/training',    icon: <FiTrendingUp />, label: 'Training',    roles: ['admin','manager','team'], perm: 'training' },
      { path: '/shifts',      icon: <FiCheckCircle />,label: 'Shifts',      roles: ['admin','manager','team'], perm: 'shifts' },
    ],
  },
  {
    key: 'work',
    label: 'Time & Work',
    items: [
      { path: '/time-tracking',  icon: <FiClock />,       label: 'Time Tracking',  roles: ['admin','manager','team'], perm: 'time_tracking' },
      { path: '/daily-reports',  icon: <FiClipboard />,   label: 'Daily Reports',  roles: ['admin','manager','team'], perm: 'daily_reports' },
      { path: '/wfh',           icon: <FiWfh />,         label: 'WFH Requests',  roles: ['admin','manager','team'], perm: 'wfh' },
      { path: '/workload',      icon: <FiActivity />,    label: 'Workload',      roles: ['admin','manager'], perm: 'workload' },
      { path: '/time-approval', icon: <FiCheckCircle />, label: 'Time Approval', roles: ['admin','manager'], perm: 'time_approval' },
      { path: '/templates',     icon: <FiLayout />,      label: 'Templates',     roles: ['admin','manager'], perm: 'templates' },
      { path: '/calendar',      icon: <FiCalendar />,    label: 'Calendar',      roles: ['admin','manager','team'], perm: 'calendar' },
    ],
  },
  {
    key: 'finance',
    label: 'Finance & CRM',
    items: [
      { path: '/analytics',       icon: <FiBarChart2 />,  label: 'Analytics',        roles: ['admin','manager'], perm: 'analytics' },
      { path: '/profit-analytics',   icon: <FiTrendingUp />,  label: 'Profit Analytics',   roles: ['admin','manager'], perm: 'profit_analytics' },
      { path: '/revenue-forecast',   icon: <FiActivity />,    label: 'Revenue Forecast',   roles: ['admin','manager'], perm: 'revenue_forecast' },
      { path: '/invoices',          icon: <FiFileText />,   label: 'Invoices',          roles: ['admin','manager'], perm: 'invoices' },
      { path: '/payment-accounts', icon: <FiDollarSign />, label: 'Payment Accounts',  roles: ['admin','manager'], perm: 'payment_accounts' },
      { path: '/expenses',         icon: <FiTrendingUp />, label: 'Expenses',           roles: ['admin','manager','team'], perm: 'expenses' },
      { path: '/employee-accounts',icon: <FiDollarSign />, label: 'Employee Accounts', roles: ['admin','manager'], perm: 'employee_accounts' },
      { path: '/clients',   icon: <FiUserPlus />,   label: 'Clients',   roles: ['admin','manager','team'], perm: 'clients' },
      { path: '/crm',       icon: <FiTrendingUp />, label: 'CRM Pipeline', roles: ['admin','manager'], perm: 'crm' },
      { path: '/upwork',    icon: <FiBriefcase />,  label: 'Upwork',       roles: ['admin','manager'], perm: 'upwork' },
    ],
  },
  {
    key: 'general',
    label: null,
    items: [
      { path: '/messages',      icon: <FiMessageSquare />, label: 'Messages',      roles: ['admin','manager','team'], perm: 'messages', isMessageBadge: true },
      { path: '/notifications', icon: <FiBell />,          label: 'Notifications', roles: ['admin','manager','team'], perm: 'notifications', isBadge: true },
      { path: '/permissions',   icon: <FiShield />,        label: 'Permissions',   roles: ['admin','manager'], perm: 'permissions' },
      { path: '/audit-log',     icon: <FiActivity />,      label: 'Audit Log',     roles: ['admin','manager'], perm: 'audit_log' },
      { path: '/settings',      icon: <FiSettings />,      label: 'Settings',      roles: ['admin','manager','team'], perm: 'settings' },
    ],
  },
];

/* ── default open state (all open) ─────────────────────── */
const DEFAULT_OPEN = NAV_GROUPS.reduce((acc, g) => {
  if (g.label) acc[g.key] = true;
  return acc;
}, {});

const STORAGE_KEY = 'sidebar_groups_open';

const Sidebar = ({ isOpen, mobileOpen, isMobile }) => {
  const { user, logout, hasPermission } = useAuth();
  const [unread, setUnread] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [showSalaryToEmployees, setShowSalaryToEmployees] = useState(true); // default visible until loaded
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : DEFAULT_OPEN;
    } catch { return DEFAULT_OPEN; }
  });

  /* poll unread notifications */
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const r = await notificationAPI.getAll({ unreadOnly: true, limit: 1 });
        setUnread(r.data.unreadCount || 0);
      } catch { /* silent */ }
    };
    fetchUnread();
    const iv = setInterval(fetchUnread, 60000);
    // Immediately refresh badge when user marks notification as read
    window.addEventListener('pm-notif-read', fetchUnread);
    return () => { clearInterval(iv); window.removeEventListener('pm-notif-read', fetchUnread); };
  }, []);

  /* poll unread messages */
  useEffect(() => {
    const fetchMsgs = async () => {
      try {
        const r = await api.get('/messages/unread-count');
        setUnreadMessages(r.data.count || 0);
      } catch { /* silent */ }
    };
    fetchMsgs();
    const iv = setInterval(fetchMsgs, 30000); // poll every 30s
    return () => clearInterval(iv);
  }, []);

  /* load company setting for salary visibility */
  useEffect(() => {
    if (user?.role !== 'admin') {
      companyAPI.get().then(r => {
        const cs = r.data?.companySettings || {};
        setShowSalaryToEmployees(cs.showSalaryToEmployees === true);
      }).catch(() => {});
    }
  }, [user?.role]);

  const toggleGroup = (key) => {
    setOpenGroups(prev => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  return (
    <div
      className={`sidebar ${isOpen ? 'open' : 'closed'} ${isMobile && mobileOpen ? 'mobile-open' : ''}`}
    >
      {/* Logo */}
      <div className="sidebar-header">
        <div className="logo">
          <div className="logo-icon">PM</div>
          {isOpen && <span className="logo-text">Project Manager</span>}
        </div>
      </div>

      {/* Nav */}
      <nav className="sidebar-nav">
        {NAV_GROUPS.map(group => {
          // Filter items by role + permissions
          const visibleItems = group.items.filter(item => {
            if (!item.roles.includes(user?.role)) return false;
            // Hide salary from non-admins when setting is off
            if (item.path === '/salary' && user?.role !== 'admin' && !showSalaryToEmployees) return false;
            // For team members with a perm key, check hasPermission
            if ((user?.role === 'team' || user?.role === 'manager') && item.perm) return hasPermission(item.perm);
            return true;
          });
          if (visibleItems.length === 0) return null;

          const isCollapsible = !!group.label;
          const isExpanded    = !isCollapsible || openGroups[group.key] !== false;

          return (
            <div key={group.key} className="nav-group">
              {/* Group header */}
              {isCollapsible && isOpen && (
                <button
                  className="nav-group-header"
                  onClick={() => toggleGroup(group.key)}
                >
                  <span className="nav-group-label">{group.label}</span>
                  <span className="nav-group-chevron">
                    {isExpanded ? <FiChevronDown size={13} /> : <FiChevronRight size={13} />}
                  </span>
                </button>
              )}

              {/* Items */}
              {(isExpanded || !isOpen) && (
                <div className={`nav-group-items ${isCollapsible && !isOpen ? 'no-header' : ''}`}>
                  {visibleItems.map(item => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                      title={!isOpen ? item.label : undefined}
                    >
                      <span className="nav-icon" style={{ position: 'relative' }}>
                        {item.icon}
                        {item.isBadge && unread > 0 && (
                          <span style={{
                            position: 'absolute', top: -5, right: -5,
                            background: '#f56565', color: '#fff',
                            borderRadius: '50%', width: 16, height: 16,
                            fontSize: 9, fontWeight: 800,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            {unread > 99 ? '99+' : unread}
                          </span>
                        )}
                        {item.isMessageBadge && unreadMessages > 0 && (
                          <span style={{
                            position: 'absolute', top: -5, right: -5,
                            background: '#22c55e', color: '#fff',
                            borderRadius: '50%', width: 16, height: 16,
                            fontSize: 9, fontWeight: 800,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            {unreadMessages > 99 ? '99+' : unreadMessages}
                          </span>
                        )}
                      </span>
                      {isOpen && <span className="nav-label">{item.label}</span>}
                      {isOpen && item.isBadge && unread > 0 && (
                        <span style={{
                          marginLeft: 'auto', background: '#f56565', color: '#fff',
                          borderRadius: 12, padding: '1px 7px', fontSize: 11, fontWeight: 700,
                        }}>
                          {unread}
                        </span>
                      )}
                      {isOpen && item.isMessageBadge && unreadMessages > 0 && (
                        <span style={{
                          marginLeft: 'auto', background: '#22c55e', color: '#fff',
                          borderRadius: 12, padding: '1px 7px', fontSize: 11, fontWeight: 700,
                        }}>
                          {unreadMessages}
                        </span>
                      )}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="sidebar-footer">
        <div className="user-info">
          <div className="user-avatar" style={{ overflow:'hidden', padding:0 }}>
            {user?.avatar
              ? <img src={user.avatar} alt={user.name} style={{ width:'100%', height:'100%', objectFit:'cover', borderRadius:'50%' }}/>
              : user?.name?.charAt(0).toUpperCase()
            }
          </div>
          {isOpen && (
            <div className="user-details">
              <div className="user-name">{user?.name}</div>
              <div className="user-role">{user?.role}</div>
            </div>
          )}
        </div>
        <button className="logout-btn" onClick={logout} title="Logout">
          <FiLogOut />
          {isOpen && <span>Logout</span>}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
